import { EDITABLE_SYSTEM_PROPERTIES } from '../../layout/widgets/adaChat/utils/systemEditSchema'

// Helper to generate property list description from EDITABLE_SYSTEM_PROPERTIES
const getEditablePropertiesDescription = (): string => {
  const properties = EDITABLE_SYSTEM_PROPERTIES.map((prop) => {
    // Add type hints and descriptions for known properties
    const descriptions: Record<string, string> = {
      battery_control_scheme:
        'battery_control_scheme (string) - MUST use search_records with record_type="battery_schemes" to find available schemes (includes default and custom org-specific schemes). Default schemes include: "solar_charged" (Self-consumption), "minimize_grid_costs_tou_no_grid_charging" (Minimize Grid Import Cost), "maximize_savings" (Maximize Savings), "peak_demand_shaving" (Peak Demand Shaving), "nz_optimized" (NZ Optimized). Use the scheme\'s "id" or "name" from search results',
      autoString: 'autoString (boolean)',
      pricing_scheme_id: 'pricing_scheme_id (number|null)',
      name: 'name (string)',
      export_limit: 'export_limit (number|null)',
      is_battery_dc_connected:
        'is_battery_dc_connected (boolean) - set to true for DC-connected/DC-coupled, false for AC-connected/AC-coupled',
      is_battery_dc_connected_auto: 'is_battery_dc_connected_auto (boolean)',
      generation_override: 'generation_override (number|null)',
      self_consumption_override:
        'self_consumption_override (number|null) - override the self-consumption percentage calculation. This is a percentage value (0-100) that overrides the calculated self-consumption for the system',
      system_efficiency_override: 'system_efficiency_override (number|null)',
      calculator:
        'calculator (integer) - valid values: 1 (PVWatts v6 - Deprecated), 2 (System Advisor Model 2020.02.29.r2), 3 (MCS Fixed), 4 (PVWatts v8), 5 (Import 3rd Party), 6 (MCS + SAM Auto), 7 (System Advisor Model 2025.4.16)',
      utility_tariff_id_override: 'utility_tariff_id_override (number|null)',
      systemPanelPlacement: 'systemPanelPlacement (string) - valid values: "Roof", "Ground", "Roof & Ground"',
      weather_dataset_override:
        'weather_dataset_override (integer) - SAM physical datasets: 0 (EnergyPlus), 1 (PVGIS), 2 (NREL PSM4), 3 (CSIRO), 4 (Himawari PSM3), 5 (SUNY India)',
      load_offsettable_by_battery_fraction:
        'load_offsettable_by_battery_fraction (number|null) - fraction (0-1) of load/usage that the battery can offset. For example, 0.5 means 50% of load can be offset by battery',
      load_offsettable_by_battery_cap:
        'load_offsettable_by_battery_cap (number|null) - maximum kW of load that the battery can offset. This caps the amount of load that can be offset regardless of the fraction setting',
      global_reserve_soc_percent:
        'global_reserve_soc_percent (number|null) - usable SOC reserve (%) for load-serving battery discharge. Zero or null means disabled',
      lp_arbitrage_reserve_soc_percent:
        'lp_arbitrage_reserve_soc_percent (number|null) - usable SOC reserve (%) for grid-export battery discharge only. Zero or null means disabled',
      timezoneOffset:
        'timezoneOffset (number) - timezone offset in hours from UTC. Examples: -8 for PST (UTC-8), +5.5 for IST (UTC+5:30), 0 for UTC, +10 for AEST (UTC+10). This affects solar generation calculations and shading analysis',
    }
    return descriptions[prop] || `${prop} (any)`
  })
  return properties.join(', ')
}

export const CLIENT_SIDE_TOOLS = [
  {
    type: 'function',
    name: 'navigate',
    description: `Generate navigate action JSON to move around the OpenSolar app.

Use this tool when users ask to navigate, go to, open, view, or access parts of the app.

Examples: "go to projects", "show me projects", "open project 123", "view project", "take me to design",
"go to design", "show me contacts", "open settings".

Multiple actions: Include sequential actions in one response if you need to navigate then perform another task (e.g.,
run auto-design after navigating to design tab). The system waits for navigation to complete before executing the next action.

Common pathnames:
Home: /home
Projects List: /projects
Project - Manage & Info: /projects/{project_id}/manage
Project - Design: /projects/{project_id}/design
Project - Energy: /projects/{project_id}/energy
Project - Payments: /projects/{project_id}/payments
Project - Proposal: /projects/{project_id}/proposal
Contacts: /contacts
Transactions: /transactions
Calendar: /calendar
Events: /events
Pricing Schemes: /pricing_schemes
Payment Options: /payment_options
Proposal Templates: /proposal_templates
Utility Tariffs: /utility_tariffs
Roles: /roles
Component Management: /component_module_activations, /component_inverter_activations, /component_battery_activations,
/component_other_activations
Project Configurations: /project_configurations
Battery Schemes: /battery_schemes
Contracts: /contracts
Testimonials: /testimonials
Customer Checkout: /customer_checkout/{orgId}
Auto Design Templates: /system_templates
Control: /control

Return valid action JSON in a code block.`,
    parameters: {
      type: 'object',
      properties: {
        pathname: {
          type: ['string', 'null'],
          description:
            "The pathname of the page to navigate to (e.g., '/projects/123/design', '/contacts', '/home'). Use null if using url instead. Either pathname or url must be provided.",
        },
        url: {
          type: ['string', 'null'],
          description:
            'The full URL to navigate to. Use null if using pathname instead. Either pathname or url must be provided.',
        },
      },
      required: ['pathname', 'url'],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'display_message_to_user',
    description:
      'Show a message to the user, or preview a pending tool call for the user on screen if confirmation is required. This tool accepts a string of markdown which can be used to show a text summary to the user. Do not rely on this tool alone, voice communication is most important but this can be useful to show a text summary or message to the user.',
    parameters: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description: 'Message to show the user. Plain text is ok, but markdown is preferred.',
        },
      },
      required: ['message'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'load_auto_design_result',
    description: `Generate loadAutoDesignResult action JSON payload for loading auto-design results into studio.
Use this tool when users want to load auto-design results that have been generated. This can only be actioned when you are in studio with the matching project already loaded.
IMPORTANT: Always wait for the user to confirm before loading auto-design results by populating buttonLabel. Do not auto-trigger this action unless the user has explicitly confirmed.
After auto design completes, a message will appear in the chat with a "Load Results" button. The user must click this button to load the design into the scene.
If you are not confident of the project_id or identifier args, just leave them as null.
The tool returns properly formatted action JSON that you MUST include in your response to the user in a JSON code block.`,
    parameters: {
      type: 'object',
      properties: {
        project_id: {
          type: ['integer', 'null'],
          description: 'The ID of the project. If project_id not specified, just use null.',
        },
        identifier: {
          type: ['string', 'null'],
          description: 'Identifier for the result to load. If the user does not specify an identifier, just use null.',
        },
        button_label: {
          type: ['string', 'null'],
          description:
            "Label for the button (default: 'Load Auto-Design Result'). Always populate this to require user confirmation before loading.",
        },
      },
      required: ['project_id', 'identifier', 'button_label'],
      additionalProperties: false,
      strict: true,
    },
  },
  // Disabled because this can be done just as well using display_message_to_user
  {
    type: 'function',
    name: 'preview_submit_site_visit',
    description:
      'Preview the site visit values collected so far which will later be used by the submit_site_visit tool. This can be fired each time a new value is collected that is relevant to the submit_site_visit tool.',
    parameters: {
      type: 'object',
      properties: {
        project_id: {
          type: 'string',
          description: 'The ID of the project to record the site visit for',
        },
        site_notes: {
          type: ['string', 'null'],
          description: 'Detailed notes from the site visit',
        },
        roof_type: {
          type: ['string', 'null'],
          description:
            'Type of roof observed during the visit. This must be one of the valid values from the submit_site_visit tool.',
        },
        number_of_storeys: {
          type: ['integer', 'null'],
          description: 'Number of storeys in the building',
        },
        number_of_phases: {
          type: ['integer', 'null'],
          description: 'Number of electrical phases at the site',
        },
        number_of_wires: {
          type: ['integer', 'null'],
          description: 'Number of electrical wires at the site',
        },
        meter_identifier: {
          type: ['string', 'null'],
          description: 'Unique identifier for the electrical meter',
        },
        parcel_identifier: {
          type: ['string', 'null'],
          description: 'Property parcel identifier',
        },
        has_cellular_coverage: {
          type: ['boolean', 'null'],
          description: 'Whether the site has cellular coverage',
        },
        wind_region: {
          type: ['string', 'null'],
          description: 'Wind region of the site',
        },
        power_factor: {
          type: ['number', 'null'],
          description: 'Power factor of the site',
        },
      },
      required: ['project_id'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'save_system_design_as_template',
    description: `Generate saveSystemDesignAsTemplate action JSON to save the currently selected system design as a template.

CRITICAL: Use this tool when the user wants to save the system in design view. Do NOT use create_or_update_system_template
for this purpose.

Requirements: user must be in a project with a system selected in design (\`/projects/{project_id}/design\`). The action extracts
all components, settings, and configuration from the selected system to create a reusable template.

Trigger phrases: "save this system as a template", "save current design as template", "create a template from this system",
"make a template from current system", "save as template".

Always wait for user confirmation before saving by using buttonLabel. "name" is required; "description" is optional.

Use create_or_update_system_template only for creating a template from scratch or when NOT in design view.

Return valid action JSON in a code block.`,
    parameters: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Name for the template (required).',
        },
        description: {
          type: ['string', 'null'],
          description: 'Optional description for the template.',
        },
        confirmed: {
          type: ['boolean', 'null'],
          description:
            'Set to true if the user has explicitly confirmed they want to save the system as a template. REQUIRED when calling this tool - if the user has confirmed (e.g., said "yes", "please", "go ahead", "confirmed"), you MUST set this to true. If the user has not confirmed, do not call this tool - instead ask for confirmation first.',
        },
        button_label: {
          type: ['string', 'null'],
          description:
            "Label for the button (default: 'Save as Template'). Always populate this to require user confirmation before saving.",
        },
      },
      required: ['name', 'description', 'button_label'],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'run_auto_design',
    description: `Generate autoDesignEphemeral action JSON to run auto-design.

CRITICAL: User must be in design tab (/projects/{project_id}/design) and design must be ready. If not, navigate there first.

SYSTEM TEMPLATE REQUIRED: Auto-design cannot run without a system template. Workflow:
1. Always search_records with record_type="system_templates" first.
2. Never guess a template; identify explicitly.
3. If user specifies template (name or ID), verify it exists via search_records.
4. If user does not specify:
   - One template exists: use automatically.
   - Multiple templates: list names/IDs, ask "Which template would you like to use?"
   - No templates: inform user to create one via create_or_update_system_template.
5. Pass template to tool using system_templates_json (preferred), template_id, or template_name.

CRITICAL: Do not assume a template. Always ask if multiple exist. No heuristics.

CRITICAL FOLLOW-UP: If user answers "which project?" or "which template?", treat as continuation:
navigate if needed, get templates, select specified template, run auto-design—all in one response.

Return valid action JSON in a code block.`,
    parameters: {
      type: 'object',
      properties: {
        system_templates_json: {
          type: ['array', 'null'],
          description:
            "REQUIRED (unless template_id or template_name is provided). Full template data from search_records (PREFERRED). Pass the template object(s) you found via search_records. Should include all fields (id, name, settings, components_and_quantities, etc.). You MUST use search_records with record_type='system_templates' to find templates first. If multiple templates exist and user hasn't specified one, you MUST ask which template to use - do not assume.",
          items: {
            type: 'object',
            additionalProperties: false,
          },
        },
        template_id: {
          type: ['integer', 'null'],
          description:
            'REQUIRED (unless system_templates_json or template_name is provided). Template ID from search_records results. You MUST use search_records to find templates first, then use the ID from the results. Never guess or assume a template ID.',
        },
        template_name: {
          type: ['string', 'null'],
          description:
            'REQUIRED (unless system_templates_json or template_id is provided). Template name from search_records results. You MUST use search_records to find templates first, then use the name from the results. Never guess or assume a template name.',
        },
        confirmed: {
          type: ['boolean', 'null'],
          description:
            'Set to true if the user has explicitly confirmed they want to run auto design. REQUIRED when calling this tool - if the user has confirmed (e.g., said "yes", "please", "go ahead", "confirmed"), you MUST set this to true. If the user has not confirmed, do not call this tool - instead ask for confirmation first.',
        },
      },
      required: ['system_templates_json', 'template_id'],
      additionalProperties: false,
      strict: false,
    },
  },
  {
    type: 'function',
    name: 'create_or_update_system_template',
    description: `Create or update a system template ("Auto Design Template"). Templates define reusable solar system
configurations with components, pricing, constraints, and design settings. Provide template_id for
updates; omit for creation. If the user wants to save the currently selected system in Design view,
and use the client action saveSystemDesignAsTemplate instead—NOT this tool. Use this tool only for
scratch creation, editing an existing template, or when the user is not in design view. Auto-design 
workflow: If the user runs auto-design but has no matching templates, create one, then continue
auto-design. When the user requests a design with specific components, first search existing TEMPLATES
(system_templates). Use an existing one if it matches. Component rules: If the user names components
("Fronius", "REC370", "Tesla battery"), you MUST look up actual component codes 
(prefer lookup_component, or search_records). Only component codes go into components_and_quantities.
At least one module is mandatory—never assume defaults; ask if unspecified. Verify component types.
After creating a template, ask if they want to add details (constraints, design type enums, pricing,
mounting, inverter/battery settings, orientations, payment options). You can update via this tool 
using template_id. For auto-design, minimal templates are acceptable; users can refine later.`,
    parameters: {
      type: 'object',
      properties: {
        template_id: {
          type: ['integer', 'null'],
          description: 'Template ID for updates. Omit or set to null for new templates.',
        },
        name: {
          type: 'string',
          description: 'Template name (required for creation).',
        },
        description: {
          type: ['string', 'null'],
          description: 'Optional description of the template.',
        },
        components_and_quantities: {
          type: ['array', 'null'],
          items: {
            type: 'object',
            properties: {
              code: { type: 'string', description: 'Component code (must be looked up first)' },
              quantity: { type: 'integer', description: 'Quantity of this component' },
              component_type: {
                type: 'string',
                enum: ['module', 'inverter', 'battery', 'other'],
                description:
                  'Type of component - MUST match the actual component type. Verify the code corresponds to this type.',
              },
            },
            required: ['code', 'quantity', 'component_type'],
            additionalProperties: false,
          },
          description: `List of components and their quantities for this template. 
MUST include at least one module (solar panel) component. 
All components must be explicitly specified by the user - never add default components.`,
        },
        design_type: {
          type: ['string', 'null'],
          enum: [
            'max_fit',
            'set_system_size',
            'set_module_qty',
            'set_annual_kwh',
            'set_annual_kwh_energy_offset',
            null,
          ],
          description: 'Design type for the template.',
        },
        constraints: {
          type: ['array', 'null'],
          items: {
            type: 'object',
            properties: {
              type: {
                type: 'string',
                enum: [
                  'annual_kwh',
                  'module_quantity',
                  'module_quantity_per_array',
                  'kw',
                  'system_price',
                  'consumption_offset_percentage',
                ],
              },
              min: { type: ['number', 'null'] },
              max: { type: ['number', 'null'] },
            },
            required: ['type', 'min', 'max'],
            additionalProperties: false,
          },
          description: 'Constraints for the template (e.g., min/max module quantity, annual kWh).',
        },
        pricing_scheme_id: {
          type: ['integer', 'null'],
          description: 'Optional pricing scheme ID to associate with this template.',
        },
        mounting_config: {
          type: ['object', 'null'],
          properties: {
            mounting_id: { type: ['string', 'null'] },
          },
          required: ['mounting_id'],
          additionalProperties: false,
          description: 'Optional mounting configuration.',
        },
        inverter_design_assistant: {
          type: ['object', 'null'],
          properties: {
            codes: {
              type: ['string', 'null'],
              description: 'Comma-separated string of inverter codes',
            },
            inverter_type: {
              type: ['string', 'null'],
              description: "Type of inverter (e.g., 'string')",
            },
          },
          required: ['codes', 'inverter_type'],
          additionalProperties: false,
          description: 'Optional inverter design assistant configuration.',
        },
        battery_design_assistant: {
          type: ['object', 'null'],
          properties: {
            codes: {
              type: ['string', 'null'],
              description: 'Comma-separated string of battery codes',
            },
            customer_goal: {
              type: ['string', 'null'],
              description: "Customer goal (e.g., 'self_consumption')",
            },
            backup_hours: {
              type: ['number', 'null'],
              description: 'Backup hours',
            },
            continuous_power: {
              type: ['number', 'null'],
              description: 'Continuous power',
            },
            peak_power: {
              type: ['number', 'null'],
              description: 'Peak power',
            },
          },
          required: ['codes', 'customer_goal', 'backup_hours', 'continuous_power', 'peak_power'],
          additionalProperties: false,
          description: 'Optional battery design assistant configuration.',
        },
        orientation_options: {
          type: ['string', 'null'],
          description: 'Optional orientation constraint.',
        },
        azimuth_from_to: {
          type: ['array', 'null'],
          items: { type: ['number', 'null'] },
          minItems: 2,
          maxItems: 2,
          description: 'Optional azimuth range [from, to] in degrees (0-360).',
        },
        payment_options_override: {
          type: ['array', 'null'],
          items: { type: ['integer', 'null'] },
          description: 'Optional list of payment option IDs.',
        },
        incentives_override: {
          type: ['array', 'null'],
          items: { type: ['integer', 'null'] },
          description: 'Optional list of incentive IDs.',
        },
        costing_override: {
          type: ['integer', 'null'],
          description: 'Optional costing override ID.',
        },
        commission_override_id: {
          type: ['integer', 'null'],
          description: 'Optional commission override ID.',
        },
        battery_control_scheme_override: {
          type: ['string', 'null'],
          description: 'Optional battery control scheme override.',
        },
      },
      required: [
        'template_id',
        'name',
        'description',
        'components_and_quantities',
        'design_type',
        'constraints',
        'pricing_scheme_id',
        'mounting_config',
        'inverter_design_assistant',
        'battery_design_assistant',
        'orientation_options',
        'azimuth_from_to',
        'payment_options_override',
        'incentives_override',
        'costing_override',
        'commission_override_id',
        'battery_control_scheme_override',
      ],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'lookup_component',
    description:
      'Look up components (solar panels/modules, inverters, batteries, or other components) by code, manufacturer name, or description. This function provides optimized search with intelligent ranking - exact code matches appear first, followed by partial matches. This is a composable tool that can be used for various purposes: 1) Finding component codes for creating system templates, 2) Verifying component availability when users mention specific components, 3) Matching user preferences (e.g., "REC panels", "Fronius inverter") to actual components, 4) Checking if templates contain specific components or manufacturers, 5) Discovering components when users have preferences but don\'t know exact codes. IMPORTANT: Always use the "code" field from results when creating system templates or referencing components in other operations. The code is the unique identifier required for component operations. When matching user preferences to templates, use this tool to find component codes, then check if templates contain those codes.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Search query - can be a component code (e.g., "REC370", "Fronius-10.0"), manufacturer name (e.g., "REC", "Fronius"), or description. For best results, use exact component codes when known.',
        },
        component_type: {
          type: ['string', 'null'],
          description:
            'Optional: Filter by component type. Options: "module", "inverter", "battery", "other". If the user specifies a type (e.g., "solar panel", "inverter", "battery"), use the corresponding value here. Omit this parameter if no type filter is needed.',
        },
        limit: {
          type: ['integer', 'null'],
          description:
            'Maximum number of results to return. Default is 10. Use 1-5 for exact code lookups, 10-20 for broader searches.',
        },
        exact_match_only: {
          type: ['boolean', 'null'],
          description:
            'If true, only return exact code matches (case-insensitive). Use this when the user provides a specific component code. Default is false.',
        },
      },
      required: ['query'],
      additionalProperties: false,
      strict: false,
    },
  },
  {
    type: 'function',
    name: 'run_auto_string',
    description: `Generate runAutoString action JSON for automatically stringing panels to inverters.
Trigger phrases: "string my inverter(s)", "string the inverter", "string panels", "auto string", "run auto string",
"automatically string panels". Auto String replaces existing stringing—inform the user before running it.
Requirements: user in design tab; a system selected; ≥1 inverter; panels placed; exactly one panel type; exactly one inverter
type. If any requirement fails, errors include: "requires at least one inverter", "requires panels", "multiple panels present",
"only available with a single inverter type".
Principles: Ensure design tab + system selected, validate system, warn about replacing current stringing. If uncertain user wants
to proceed, use buttonLabel for confirmation.
Return valid action JSON in a code block.`,
    parameters: {
      type: 'object',
      properties: {
        button_label: {
          type: ['string', 'null'],
          description:
            "Label for the button (default: 'Run Auto String'). Populate this if you want to require user confirmation before running auto string.",
        },
      },
      confirmed: {
        type: ['boolean', 'null'],
        description:
          'Set to true if the user has explicitly confirmed they want to run auto string. REQUIRED when calling this tool - if the user has confirmed (e.g., said "yes", "please", "go ahead", "confirmed"), you MUST set this to true. If the user has not confirmed, do not call this tool - instead ask for confirmation first.',
      },
      required: ['button_label', 'confirmed'],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'run_battery_design_assistant',
    description: `Generate runBatteryDesignAssistant action JSON payload for getting battery recommendations.

CRITICAL - USE THIS TOOL (NOT lookup_component): When users ask for battery "recommendations", "suggestions", or "what would work best", you MUST use this tool. Examples: "suggest me a battery", "what batteries would work", "recommend batteries", "find battery options", "get battery recommendations", "what battery should I use". Only use lookup_component when users want to search for specific battery codes or verify component details - NOT for recommendations.

CRITICAL: Always ask probing questions FIRST to understand the user's objectives (customer goal, backup hours, continuous power, peak power) before executing. If the user provides some but not all information, ask follow-up questions to collect missing details.

IMPORTANT - Probing Questions Required:
1. Customer Goal (optional, defaults to "self_consumption"): "self_consumption" (maximize self-consumption), "maximize_savings" (maximize financial savings), or "peak_demand_shaving" (reduce peak demand charges)
2. Backup Requirements (optional): backup_hours (hours of backup power needed), continuous_power_kw (average continuous power in kW), peak_power_kw (peak power in kW)
3. Battery Filtering (optional): codes (array of specific battery codes to limit recommendations), manufacturer (manufacturer name to filter by, e.g., "Fox ESS", "Tesla", "LG")

MANUFACTURER FILTERING: If the user specifies a manufacturer (e.g., "suggest me the best Fox ESS Battery" or "recommend Tesla batteries"), you MUST:
1. Use lookup_component with query=manufacturer_name and component_type="battery" to find all batteries from that manufacturer
2. Extract the codes from the lookup results
3. Pass those codes to the "codes" parameter to limit recommendations to that manufacturer
4. Do NOT pass the manufacturer parameter directly - convert it to codes first

CRITICAL: Once the user has provided all the required information (customer goal, backup hours, continuous power, peak power), you MUST call this tool immediately - providing all requested information is considered implicit confirmation.

Requirements:
- The user must be in the design tab (/projects/{project_id}/design)
- A system must be selected in the design
- The system must have panels placed (panels are required for battery recommendations)

The tool returns properly formatted action JSON that you MUST include in your response to the user in a JSON code block.`,
    parameters: {
      type: 'object',
      properties: {
        customer_goal: {
          type: ['string', 'null'],
          description:
            'Customer goal: "self_consumption" (maximize self-consumption), "maximize_savings" (maximize financial savings), or "peak_demand_shaving" (reduce peak demand charges). Defaults to "self_consumption" if not specified.',
          enum: ['self_consumption', 'maximize_savings', 'peak_demand_shaving'],
        },
        backup_hours: {
          type: ['number', 'null'],
          description: 'How many hours of backup power are needed.',
        },
        continuous_power_kw: {
          type: ['number', 'null'],
          description: 'Average continuous power requirement in kilowatts.',
        },
        peak_power_kw: {
          type: ['number', 'null'],
          description: 'Peak power requirement in kilowatts.',
        },
        codes: {
          type: ['array', 'null'],
          description:
            'Optional array of specific battery codes to limit recommendations to. If manufacturer is specified, you MUST first use lookup_component to find batteries from that manufacturer, then pass their codes here.',
          items: {
            type: 'string',
          },
        },
        manufacturer: {
          type: ['string', 'null'],
          description:
            "Optional manufacturer name to filter recommendations by (e.g., 'Fox ESS', 'Tesla', 'LG'). If provided, you MUST use lookup_component with query=manufacturer_name and component_type='battery' to find all batteries from this manufacturer, extract their codes, and pass those codes to the 'codes' parameter. Do NOT pass manufacturer directly - convert it to codes first.",
        },
        num_recommendations: {
          type: ['integer', 'null'],
          description: 'Number of recommendations to return. Default is 3.',
        },
        confirmed: {
          type: ['boolean', 'null'],
          description:
            'Set to true if the user has explicitly confirmed OR if the user has provided all required information (customer goal, backup hours, continuous power, peak power). REQUIRED when calling this tool via voice/tool calls - providing all requested parameters is considered implicit confirmation. If the user has confirmed (e.g., said "yes", "please", "go ahead", "confirmed") OR provided all the information you asked for, you MUST set this to true. If the user has not provided all information and has not confirmed, do not call this tool - instead ask for the missing information first.',
        },
      },
      required: [
        'customer_goal',
        'backup_hours',
        'continuous_power_kw',
        'peak_power_kw',
        'codes',
        'num_recommendations',
        'manufacturer',
        'confirmed',
      ],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'run_inverter_design_assistant',
    description: `Generate runInverterDesignAssistant action JSON for inverter recommendations.
Use this tool (NOT lookup_component) when users ask for inverter suggestions: "suggest/recommend inverters", "what inverters
should I use", "find inverters for this system". Use lookup_component only when searching for specific inverter codes.
The assistant analyzes the system's panel configuration and returns ranked inverter recommendations based on module clusters
and system requirements.
CRITICAL: Always confirm inverter type BEFORE running the assistant. Ask user for type if not given. Options include:
- Central inverters (also called string inverters)
- Microinverters
- Power optimised inverters (with optimizers)
- Hybrid inverters
Manufacturer filter (optional): If user specifies a manufacturer, use lookup_component (component_type="inverter") to get all
matching inverter codes. Pass those codes into the "codes" parameter. Never pass manufacturer name directly.
Requirements: User must be in design tab, a system must be selected, panels must be placed, and design must be fully loaded.
The tool shows progress and then displays ranked recommendations with UI selection.
Return valid action JSON in a code block.`,
    parameters: {
      type: 'object',
      properties: {
        inverter_type: {
          type: ['string', 'null'],
          description:
            'Filter by inverter type: "string" (central inverters), "micro" (microinverters), "power_optimized" (power optimised inverters with optimizers), or "hybrid" (hybrid inverters). This is REQUIRED - do not call this tool without knowing the inverter type preference.',
          enum: ['string', 'micro', 'power_optimized', 'hybrid'],
        },
        codes: {
          type: ['array', 'null'],
          description:
            "Optional array of specific inverter codes to limit recommendations to. If user specifies a manufacturer, you MUST first use lookup_component with query=manufacturer_name and component_type='inverter' to find all inverters from that manufacturer, extract their codes, and pass those codes here.",
          items: {
            type: 'string',
          },
        },
        limit: {
          type: ['integer', 'null'],
          description: 'Maximum number of recommendations to return. Default is 20.',
        },
        confirmed: {
          type: ['boolean', 'null'],
          description:
            'Set to true if the user has explicitly confirmed they want to get inverter recommendations. REQUIRED when calling this tool - if the user has confirmed (e.g., said "yes", "please", "go ahead", "confirmed"), you MUST set this to true. If the user has not confirmed, do not call this tool - instead ask for confirmation first.',
        },

        button_label: {
          type: ['string', 'null'],
          description:
            "Label for the button (default: 'Get Inverter Recommendations'). Populate this if you want to require user confirmation.",
        },
      },
      required: ['inverter_type', 'codes', 'limit', 'button_label', 'confirmed'],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'load_battery_design_assistant_result',
    description:
      'Load a selected battery recommendation into the system. This will replace any existing batteries. CRITICAL: When the user says "load option 1", "load the first option", "select option 1", etc., you MUST convert this to 0-based index: "option 1" = batterySetIndex 0, "option 2" = batterySetIndex 1, "option 3" = batterySetIndex 2. The batterySetIndex parameter is 0-based, so subtract 1 from the user\'s option number. This does not require confirmation.',
    parameters: {
      type: 'object',
      properties: {
        batterySetIndex: {
          type: 'integer',
          description:
            'Index of the battery recommendation to load (0-based). IMPORTANT: If user says "option 1", use 0. If user says "option 2", use 1. If user says "option 3", use 2. Always subtract 1 from the user\'s option number.',
        },
      },
      required: ['batterySetIndex'],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'load_inverter_design_assistant_result',
    description:
      'Load a selected inverter recommendation into the system. This will replace any existing inverters. CRITICAL: When the user says "load option 1", "load the first option", "select option 1", etc., you MUST convert this to 0-based index: "option 1" = inverterSetIndex 0, "option 2" = inverterSetIndex 1, "option 3" = inverterSetIndex 2. The inverterSetIndex parameter is 0-based, so subtract 1 from the user\'s option number. This does not require confirmation.',
    parameters: {
      type: 'object',
      properties: {
        inverterSetIndex: {
          type: 'integer',
          description:
            'Index of the inverter recommendation to load (0-based). IMPORTANT: If user says "option 1", use 0. If user says "option 2", use 1. If user says "option 3", use 2. Always subtract 1 from the user\'s option number.',
        },
      },
      required: ['inverterSetIndex'],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'system_edit',
    description: `Generate systemEdit action JSON for modifying system designs. Use this tool for ANY system change except adding panels. Covers
battery AC/DC (is_battery_dc_connected), export limits, pricing scheme, battery control scheme, incentives, naming, panel
placement (roof/ground), adding/removing/updating components. Panel/module edits = Auto Design only.
Component ops: Check APP_STATE.design.systems.summary first. Identify selected system (or first). If only one battery/inverter/
other exists, auto-use its uuid/code/id. If multiple, list them (manufacturer/code/qty) and ask which to update. For adding
components, lookup activation IDs via lookup_component (preferred) or search_records.
Proactive lookups: If user requests pricing scheme, tariff, incentives, battery control scheme without specifying, search
available options (pricing_schemes, utility_tariffs, battery_schemes) and present choices.
Editable properties: battery_control_scheme, name, export_limit, pricing_scheme_id, is_battery_dc_connected,
systemPanelPlacement, calculator, utility_tariff_id_override, weather dataset, overrides, timezoneOffset. Validate values via
search_records when IDs/schemes apply.
Battery control schemes: always search battery_schemes (includes org-custom). Use scheme id or name.
Return valid action JSON in a code block.`,
    parameters: {
      type: 'object',
      properties: {
        operations: {
          type: ['array', 'null'],
          description: `Array of operations to perform. CRITICAL: Each operation MUST have a "type" field. Valid operation types: "add", "remove", "update", "updateProperty", "addIncentive", "removeIncentive", "updateIncentive". Operation formats:
1) add: {"type": "add", "componentType": "inverter"|"battery"|"other", "componentId": integer, "quantity": integer (optional), "slotKey": string (optional)}
2) remove: {"type": "remove", "componentType": "inverter"|"battery"|"other", "componentUuid": string}
3) update: {"type": "update", "componentType": "battery"|"other", "componentUuid": string (optional), "componentCode": string (optional), "componentId": number (optional), "quantity": integer}. CRITICAL FOR UPDATE - PROACTIVE COMPONENT IDENTIFICATION: Before calling this operation, you MUST check APP_STATE.design.systems.summary array. Find the system where isSelected: true (or first system). Each system has structured arrays: "batteries" [{uuid, code, battery_id, quantity, manufacturer_name}], "inverters" [{uuid, code, inverter_id, manufacturer_name}], "others" [{uuid, code, other_id, quantity, manufacturer_name}]. If there is only ONE component of that type, automatically use its componentCode (from "code" field) or componentId (from "battery_id"/"inverter_id"/"other_id") - do NOT ask the user. If there are MULTIPLE, list them with codes/quantities/manufacturers and ask which one. You MUST provide at least ONE identifier: componentUuid (from "uuid"), componentCode (from "code"), or componentId (from "battery_id"/"inverter_id"/"other_id"). Always check APP_STATE.design.systems.summary first.
4) updateProperty: {"type": "updateProperty", "property": string, "value": any}
  Valid properties (from EDITABLE_SYSTEM_PROPERTIES): battery_control_scheme (string) - MUST use search_records with record_type="battery_schemes" to find available schemes (includes default and custom org-specific schemes). Default schemes include: "solar_charged" (Self-consumption), "minimize_grid_costs_tou_no_grid_charging" (Minimize Grid Import Cost), "maximize_savings" (Maximize Savings), "peak_demand_shaving" (Peak Demand Shaving), "nz_optimized" (NZ Optimized). Use the scheme's "id" or "name" from search results; autoString (boolean); pricing_scheme_id (integer|null); name (string); export_limit (number|null); is_battery_dc_connected (boolean) - set to true for DC-connected/DC-coupled, false for AC-connected/AC-coupled; is_battery_dc_connected_auto (boolean); generation_override (number|null); self_consumption_override (number|null) - override the self-consumption percentage calculation (0-100); system_efficiency_override (number|null); calculator (integer) - valid values: 1 (PVWatts v6 - Deprecated), 2 (System Advisor Model 2020.02.29.r2), 3 (MCS Fixed), 4 (PVWatts v8), 5 (Import 3rd Party), 6 (MCS + SAM Auto), 7 (System Advisor Model 2025.4.16); utility_tariff_id_override (integer|null); systemPanelPlacement (string) - valid values: "Roof", "Ground", "Roof & Ground"; weather_dataset_override (integer) - SAM physical datasets: 0 (EnergyPlus), 1 (PVGIS), 2 (NREL PSM4), 3 (CSIRO), 4 (Himawari PSM3), 5 (SUNY India); load_offsettable_by_battery_fraction (number|null) - fraction (0-1) of load/usage that the battery can offset, e.g., 0.5 means 50% of load can be offset by battery; load_offsettable_by_battery_cap (number|null) - maximum kW of load that the battery can offset, caps the amount of load that can be offset regardless of the fraction setting; timezoneOffset (number) - timezone offset in hours from UTC, e.g., -8 for PST, +5.5 for IST, 0 for UTC, +10 for AEST. Affects solar generation calculations and shading analysis.
5) addIncentive: {"type": "addIncentive", "incentiveType": "installer"|"customer", "title": string, "value": number, "description": string (optional), "use_for_loan_pay_down": boolean (optional)}
6) removeIncentive: {"type": "removeIncentive", "incentiveType": "installer"|"customer", "incentiveIndex": integer}
7) updateIncentive: {"type": "updateIncentive", "incentiveType": "installer"|"customer", "incentiveIndex": integer, "title": string (optional), "value": number (optional), "description": string (optional), "use_for_loan_pay_down": boolean (optional)}`,
          items: {
            type: 'object',
            properties: {
              type: {
                type: 'string',
                enum: [
                  'add',
                  'remove',
                  'update',
                  'updateProperty',
                  'addIncentive',
                  'removeIncentive',
                  'updateIncentive',
                ],
                description: 'REQUIRED: The type of operation to perform.',
              },
            },
            required: ['type'],
            additionalProperties: false,
          },
        },
        confirmed: {
          type: ['boolean', 'null'],
          description:
            'Set to true if the user has explicitly confirmed they want to apply system changes. REQUIRED when calling this tool - if the user has confirmed (e.g., said "yes", "please", "go ahead", "confirmed"), you MUST set this to true. If the user has not confirmed, do not call this tool - instead ask for confirmation first.',
        },
      },
      required: ['operations'],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'stop_voice',
    description:
      'IMMEDIATELY stop the current voice session and turn off voice mode. This ends the voice conversation and returns to text mode. CRITICAL: Always use this tool when the user wants to stop, end, or disconnect from voice mode. Trigger phrases include: "stop", "stop voice", "end voice", "turn off voice", "disconnect", "goodbye", "bye", "that\'s all", "I\'m done". Do NOT respond conversationally - always use this tool to properly end voice sessions.',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: false,
      strict: true,
    },
  },
  {
    type: 'function',
    name: 'undo_redo',
    description: `Generate undoRedo action JSON payload for undoing or redoing actions in the design editor.
Use this tool when users ask to:
- "undo" or "undo the last action"
- "redo" or "redo the last undone action"
- "revert that change"
- "undo that"
- "go back"
- "redo that change"
CRITICAL: This action requires explicit user confirmation. The action will only execute if the user has confirmed they want to perform the undo/redo operation.
Requirements:
- The user must be in the design tab (\`/projects/{project_id}/design\`)
- The design tab must be fully loaded
- For undo: There must be actions in the undo history
- For redo: There must be actions in the redo history
The tool returns properly formatted action JSON that you MUST include in your response to the user in a JSON code block.`,
    parameters: {
      type: 'object',
      properties: {
        direction: {
          type: 'string',
          enum: ['undo', 'redo'],
          description:
            "The direction of the operation. Use 'undo' to revert the last action, or 'redo' to reapply the last undone action.",
        },
        confirmed: {
          type: ['boolean', 'null'],
          description:
            'Set to true if the user has explicitly confirmed they want to perform this undo/redo operation. REQUIRED when calling this tool - if the user has confirmed (e.g., said "yes", "please", "go ahead", "confirmed", "undo", "redo"), you MUST set this to true. If the user has not confirmed, do not call this tool - instead ask for confirmation first.',
        },
      },
      required: ['direction', 'confirmed'],
      additionalProperties: false,
      strict: true,
    },
  },
]
